import { defineStore } from "pinia";

import { tagService } from "@/services";
import { isApiError } from "@/services/Api";

import { ModuleStatus } from "@/models/ModuleStatus";

import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { pollingEvents } from "@/stores/plugins/polling-events";

import type { TagType } from "@/models/TagType";
import type { Tag, FileTag } from "@/models/Tag";

interface State {
  tags: readonly Tag[];
  fileTags: readonly FileTag[];
  moduleStatus: ModuleStatus;
  tagsObject: Record<string, readonly Tag[]>;
  tagTypes: readonly TagType[];
}
export const useTagStore = defineStore("tag", {
  state: (): State => ({
    tags: [],
    fileTags: [],
    moduleStatus: ModuleStatus.Initial,
    tagsObject: {},
    tagTypes: [],
  }),
  getters: {},
  actions: {
    async load(): Promise<void> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.tags === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      // Run these in parallel
      this.setStatus(ModuleStatus.Loading);

      const promises: [Promise<TagType[]>, Promise<Tag[]>, Promise<FileTag[]>] = [
        tagService.getTagTypes(brandStore.selectedClient, brandStore.selectedBrand),
        tagService.getAllTags(brandStore.selectedClient, brandStore.selectedBrand, selectedCaseName),
        tagService.getAllFileTags(brandStore.selectedClient, brandStore.selectedBrand, selectedCaseName),
      ];

      // Wait for all results to finish, not just the first rejection
      const results = await Promise.allSettled(promises);

      this.tagTypesLoaded(results[0].status === "fulfilled" ? results[0].value || [] : []);
      this.tagsLoaded(results[1].status === "fulfilled" ? results[1].value || [] : []);
      this.fileTagsLoaded(results[2].status === "fulfilled" ? results[2].value || [] : []);

      this.setStatus(ModuleStatus.Ready);
    },
    async loadFileTags(): Promise<void> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.tags === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      try {
        this.setStatus(ModuleStatus.Loading);

        const fileTags = await tagService.getAllFileTags(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
        );
        this.fileTagsLoaded(fileTags || []);

        this.setStatus(ModuleStatus.Ready);
      } catch (e) {
        if (isApiError(e) && e.status === 204) {
          this.fileTagsLoaded([]);
        } else {
          throw e;
        }
      }
    },
    async saveTag(payload: { name: string; tagType: string }): Promise<void> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.tags === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      try {
        const tag = {
          brand: brandStore.selectedBrand,
          caseReference: selectedCaseName,
          tagType: payload.tagType,
          name: payload.name,
        };

        this.setStatus(ModuleStatus.Loading);
        await tagService.saveTag(brandStore.selectedClient, tag);
        this.tagAdded(tag);
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },
    isDuplicate(payload: { name: string }): boolean {
      return this.tags.findIndex((t) => t.name === payload.name) !== -1;
    },
    async deleteTag(payload: { name: string }): Promise<void> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.tags === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      try {
        const tag = {
          brand: brandStore.selectedBrand,
          caseReference: selectedCaseName,
          tagType: "unknown",
          name: payload.name,
        };

        this.setStatus(ModuleStatus.Loading);
        await tagService.deleteTag(brandStore.selectedClient, tag);
        this.tagDeleted(tag);
        await this.loadFileTags();
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },
    async assignTag(payload: { fileReference: string; name: string }): Promise<void> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.tags === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      try {
        this.setStatus(ModuleStatus.Loading);

        await tagService.saveFileTag(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          payload.fileReference,
          payload.name,
        );

        await this.loadFileTags();
        pollingEvents.restart();
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },
    async unAssignTag(payload: { fileReference: string; name: string }): Promise<void> {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.tags === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      try {
        this.setStatus(ModuleStatus.Loading);
        await tagService.deleteFileTag(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          payload.fileReference,
          payload.name,
        );

        await this.loadFileTags();
        pollingEvents.restart();
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },

    // Mutation
    setStatus(moduleStatus: ModuleStatus): void {
      this.moduleStatus = moduleStatus;
    },
    tagsLoaded(payload: Tag[]): void {
      this.tags = Object.freeze(payload);
    },
    tagAdded(payload: Tag): void {
      this.tags = Object.freeze(this.tags.concat([payload]));
    },
    tagDeleted(payload: Tag): void {
      this.tags = Object.freeze(this.tags.filter((t) => t.name !== payload.name));
    },
    tagTypesLoaded(payload: TagType[]): void {
      this.tagTypes = Object.freeze(payload);
    },
    fileTagsLoaded(payload: FileTag[]): void {
      this.fileTags = Object.freeze(payload);
      this.tagsObject = {};
      payload.forEach((t) => {
        if (!this.tagsObject[t.fileReference]) {
          this.tagsObject[t.fileReference] = [];
        }
        this.tagsObject[t.fileReference] = Object.freeze(
          this.tagsObject[t.fileReference].concat([
            {
              ...t,
              classSafeTagType: t.tagType.replace(/\s/g, "").toLowerCase(),
              classSafeName: t.name.replace(/\s/g, "").toLowerCase(),
            },
          ]),
        );
      });
    },
    reset(): void {
      this.moduleStatus = ModuleStatus.Initial;
      this.tagTypes = [];
      this.tags = [];
      this.tagsObject = {};
    },
  },
});
